/**
 * Field Validation Hook
 *
 * Validates patient data before creating/updating in Tebra EMR.
 * Matches desktop validation logic in gui/data_fields.py:validate_all_fields()
 *
 * Required fields:
 * - Patient: LastName, FirstName, DateOfBirth, Gender
 * - Insurance: If subscriber is not patient, validate subscriber fields
 */

import type { PDFMetadata } from '../types/api';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const useFieldValidation = () => {
  /**
   * Validate all required fields using parsed widget columns from PDFMetadata
   */
  const validateAllFields = (pdf: PDFMetadata | null): ValidationResult => {
    const errors: string[] = [];

    if (!pdf) {
      errors.push('No PDF data available');
      return { isValid: false, errors };
    }

    // Patient Demographics - Required fields
    if (!pdf.LastName || pdf.LastName.trim() === '') {
      errors.push('Patient Last Name is required');
    }

    if (!pdf.FirstName || pdf.FirstName.trim() === '') {
      errors.push('Patient First Name is required');
    }

    if (!pdf.DateOfBirth || pdf.DateOfBirth.trim() === '') {
      errors.push('Patient Date of Birth is required');
    }

    if (!pdf.Gender || pdf.Gender.trim() === '') {
      errors.push('Patient Gender is required');
    }

    // Primary Insurance - Conditional validation
    const primaryCompany = pdf.PrimaryInsuranceCompany;
    if (primaryCompany && primaryCompany.trim() !== '') {
      const primarySubscriberIsSelf = pdf.PrimarySubscriberRelationship === 'Self';

      if (!primarySubscriberIsSelf) {
        if (!pdf.PrimarySubscriberName || pdf.PrimarySubscriberName.trim() === '') {
          errors.push('Primary Insurance: Subscriber Name is required when subscriber is not patient');
        }

        if (!pdf.PrimarySubscriberGender || pdf.PrimarySubscriberGender.trim() === '') {
          errors.push('Primary Insurance: Subscriber Gender is required when subscriber is not patient');
        }

        if (!pdf.PrimarySubscriberRelationship || pdf.PrimarySubscriberRelationship.trim() === '') {
          errors.push('Primary Insurance: Subscriber Relationship is required when subscriber is not patient');
        }
      }
    }

    // Secondary Insurance - Conditional validation
    const secondaryCompany = pdf.SecondaryInsuranceCompany;
    if (secondaryCompany && secondaryCompany.trim() !== '') {
      const secondarySubscriberIsSelf = pdf.SecondarySubscriberRelationship === 'Self';

      if (!secondarySubscriberIsSelf) {
        if (!pdf.SecondarySubscriberName || pdf.SecondarySubscriberName.trim() === '') {
          errors.push('Secondary Insurance: Subscriber Name is required when subscriber is not patient');
        }

        if (!pdf.SecondarySubscriberGender || pdf.SecondarySubscriberGender.trim() === '') {
          errors.push('Secondary Insurance: Subscriber Gender is required when subscriber is not patient');
        }

        if (!pdf.SecondarySubscriberRelationship || pdf.SecondarySubscriberRelationship.trim() === '') {
          errors.push('Secondary Insurance: Subscriber Relationship is required when subscriber is not patient');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  /**
   * Validate date format (MM/DD/YYYY)
   */
  const validateDateFormat = (date: string): boolean => {
    if (!date) return false;
    const datePattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    return datePattern.test(date);
  };

  /**
   * Validate phone format (allows various formats)
   */
  const validatePhoneFormat = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional
    const phonePattern = /^[\d\s()-]+$/;
    return phonePattern.test(phone) && phone.replace(/\D/g, '').length === 10;
  };

  /**
   * Validate email format
   */
  const validateEmailFormat = (email: string): boolean => {
    if (!email) return true; // Email is optional
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  return {
    validateAllFields,
    validateDateFormat,
    validatePhoneFormat,
    validateEmailFormat,
  };
};
